import React, { useState, useEffect } from 'react';
import './index.css';
import { sportsData } from './data';
import 'chart.js/auto';
import { Bar, Radar, Scatter, Doughnut } from 'react-chartjs-2';

// Utility functions
const byDesc = (arr, key) => [...arr].sort((a, b) => (b[key] || 0) - (a[key] || 0));
const sum = (arr, key) => arr.reduce((s, x) => s + (x[key] || 0), 0);
const avg = (arr, key) => (arr.length ? sum(arr, key) / arr.length : 0);
const format = (n, digits = 1) => Number(n).toFixed(digits);

// Advanced metric calculators
const getBasketballAdv = (players) => players.map(p => ({
  ...p, per: p.ppg + p.rpg + p.apg + p.spg + p.bpg - p.tov, off: p.ppg + p.apg, def: p.spg + p.bpg + p.rpg * 0.5
}));
const getVolleyballAdv = (players) => players.map(p => ({
  ...p, aes: p.spikes * 2 + p.aces * 2.5 + p.blocks * 1.5, total: p.aces + p.digs + p.blocks + p.spikes
}));
const getLaxAdv = (players) => players.map(p => ({
  ...p, lis: p.goals * 2 + p.assists * 1.5 + p.gbs + p.ct * 1.5, off: p.goals + p.assists, def: p.gbs + p.ct
}));

// Chart Base Options
const chartOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { labels: { color: "#dbe5ff" } }, tooltip: { backgroundColor: "#111827", titleColor: "#fff", bodyColor: "#dbe5ff" } },
  scales: { x: { ticks: { color: "#c3cde4" }, grid: { color: "rgba(255,255,255,.08)" } }, y: { ticks: { color: "#c3cde4" }, grid: { color: "rgba(255,255,255,.08)" } } }
};

function App() {
  const [theme, setTheme] = useState('dark');
  const [view, setView] = useState('home');
  const [sport, setSport] = useState(null);
  const [tier, setTier] = useState(null);
  const [season, setSeason] = useState(null);
  const [tab, setTab] = useState('overview');

  // Theme Toggle
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.style.setProperty("--bg", "#f6f8fc"); root.style.setProperty("--panel", "#ffffff");
      root.style.setProperty("--panel2", "#f3f6fb"); root.style.setProperty("--line", "#d9e1ef");
      root.style.setProperty("--text", "#0f172a"); root.style.setProperty("--muted", "#64748b");
      document.body.style.background = "linear-gradient(180deg,#edf2fb,#f8fbff)";
    } else {
      root.style.setProperty("--bg", "#0f1115"); root.style.setProperty("--panel", "#171a21");
      root.style.setProperty("--panel2", "#1f2430"); root.style.setProperty("--line", "#2e3647");
      root.style.setProperty("--text", "#eef2ff"); root.style.setProperty("--muted", "#9aa4bb");
      document.body.style.background = "linear-gradient(180deg,#0b0d12,#101521 30%,#0f1115)";
    }
  }, [theme]);

  const enterDashboard = () => { setView('dashboard'); setTab('overview'); window.scrollTo(0, 0); };
  const goHome = () => { setView('home'); window.scrollTo(0, 0); };
  const currentData = sport && tier && season ? sportsData[sport].tiers[tier].seasons[season] : null;

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <div className="logo">SJ</div>
          <div>SJA Sports Analytics</div>
        </div>
        <button className="theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>Toggle Theme</button>
      </div>

      <div className="wrap">
        {view === 'home' ? (
          <section>
            <div className="hero">
              <div className="eyebrow">School Sports Data Platform</div>
              <h1>Select your sport and season<br />to dive into the analytics.</h1>
              <div className="sub">Welcome to the SJA Sports Data Platform. Choose a sport, team tier, and season below to access real-time dashboards, top performers, and advanced metrics.</div>
            </div>

            <div className="section-label">1. Select Sport</div>
            <div className="sports-grid">
              {Object.entries(sportsData).map(([key, s]) => (
                <div key={key} className={`card sport-card ${sport === key ? 'active' : ''}`} onClick={() => { setSport(key); setTier(null); setSeason(null); }}>
                  <div className="sport-icon">{s.icon}</div>
                  <div className="sport-name">{s.label}</div>
                  <div className="sport-meta">{s.meta}</div>
                  <div className="chips">
                    {s.tiers.varsity && <span className="chip gold">Varsity</span>}
                    {s.tiers.jv && <span className="chip green">JV</span>}
                  </div>
                </div>
              ))}
            </div>

            {sport && (
              <>
                <div className="section-label">2. Select Team Tier</div>
                <div className="pick-row">
                  {Object.entries(sportsData[sport].tiers).map(([key, t]) => (
                    <button key={key} className={`pill-btn ${tier === key ? 'active' : ''}`} onClick={() => { setTier(key); setSeason(null); }}>{t.label}</button>
                  ))}
                </div>
              </>
            )}

            {sport && tier && (
              <>
                <div className="section-label">3. Select Season</div>
                <div className="pick-row">
                  {Object.entries(sportsData[sport].tiers[tier].seasons).map(([key, s]) => (
                    <button key={key} className={`pill-btn ${season === key ? 'active' : ''}`} onClick={() => setSeason(key)}>{s.label}</button>
                  ))}
                </div>
              </>
            )}

            <div style={{ marginTop: '20px' }}>
              <button className="primary-btn" disabled={!(sport && tier && season)} onClick={enterDashboard}>View Analytics Dashboard</button>
            </div>
          </section>
        ) : (
          <section>
            <div className="stats-head">
              <div>
                <div className="crumbs">Home / {sportsData[sport].label} / {sportsData[sport].tiers[tier].label} / {currentData.label}</div>
                <div className="title">{sportsData[sport].icon} {currentData.teamLabel}</div>
                <div className="sub">{currentData.summary}</div>
              </div>
              <button className="ghost-btn" onClick={goHome}>Return Home</button>
            </div>

            <div className="tabs">
              {['overview', 'roster', 'charts', 'advanced'].map(id => (
                <button key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{id.toUpperCase()}</button>
              ))}
            </div>

            {/* Subcomponents Rendering */}
            {currentData.type === 'basketball' && <BasketballVarsity data={currentData} currentTab={tab} />}
            {currentData.type === 'basketball-jv' && <BasketballJV data={currentData} currentTab={tab} />}
            {currentData.type === 'volleyball' && <Volleyball data={currentData} currentTab={tab} />}
            {currentData.type === 'lacrosse' && <Lacrosse data={currentData} currentTab={tab} />}
          </section>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------
// 🏀 Basketball Varsity Component
// ----------------------------------------------------
function BasketballVarsity({ data, currentTab }) {
  const adv = getBasketballAdv(data.players);
  const topScorer = byDesc(adv, "ppg")[0];
  const topReb = byDesc(adv, "rpg")[0];
  const topStl = byDesc(adv, "spg")[0];
  const topPer = byDesc(adv, "per")[0];

  return (
    <div>
      {currentTab === 'overview' && (
        <>
          <div className="card mvp">
            <small>Season Leader · Scoring</small>
            <h2>{topScorer.name}</h2>
            <div className="mvp-stats">
              <div><b>{format(topScorer.ppg)}</b><span>PPG</span></div>
              <div><b>{format(topScorer.rpg)}</b><span>RPG</span></div>
              <div><b>{format(topScorer.apg)}</b><span>APG</span></div>
              <div><b>{format(topScorer.fg)}%</b><span>FG%</span></div>
            </div>
          </div>
          <div className="kpi-grid" style={{ marginTop: '16px' }}>
            <div className="card kpi"><div className="label">Top Scorer</div><div className="value">{format(topScorer.ppg)}</div><div className="meta">{topScorer.name}</div></div>
            <div className="card kpi"><div className="label">Top Rebounder</div><div className="value">{format(topReb.rpg)}</div><div className="meta">{topReb.name}</div></div>
            <div className="card kpi"><div className="label">Top Steals</div><div className="value">{format(topStl.spg)}</div><div className="meta">{topStl.name}</div></div>
            <div className="card kpi"><div className="label">Best PER</div><div className="value">{format(topPer.per)}</div><div className="meta">{topPer.name}</div></div>
            <div className="card kpi"><div className="label">Avg FG%</div><div className="value">{format(avg(adv, "fg"))}%</div><div className="meta">Team average</div></div>
            <div className="card kpi"><div className="label">Total Points</div><div className="value">{sum(adv, "pts")}</div><div className="meta">Season total</div></div>
          </div>
          <div className="grid-2" style={{ marginTop: '16px' }}>
            <div className="card panel-pad">
              <div className="panel-title">Scoring Leaders</div>
              <div className="chart-container">
                <Bar data={{ labels: byDesc(adv, "ppg").map(x => x.name.split(" ")[0]), datasets: [{ label: "PPG", data: byDesc(adv, "ppg").map(x => x.ppg), backgroundColor: "#60a5fa" }] }} options={chartOpts} />
              </div>
            </div>
            <div className="card panel-pad">
              <div className="panel-title">FG% by Player</div>
              <div className="chart-container">
                <Bar data={{ labels: adv.map(x => x.name.split(" ")[0]), datasets: [{ label: "FG%", data: adv.map(x => x.fg), backgroundColor: "#fbbf24" }] }} options={chartOpts} />
              </div>
            </div>
          </div>
        </>
      )}

      {currentTab === 'roster' && (
        <div className="card panel-pad">
          <div className="panel-title">Full Roster Stats</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Rank</th><th>#</th><th>Player</th><th>GP</th><th>PPG</th><th>RPG</th><th>APG</th><th>SPG</th><th>FG%</th><th>PER</th></tr></thead>
              <tbody>
                {byDesc(adv, "per").map((p, i) => (
                  <tr key={p.name}>
                    <td className={`rank ${i === 0 ? 'r1' : i === 1 ? 'r2' : i === 2 ? 'r3' : ''}`}>{i + 1}</td>
                    <td>{p.num}</td><td>{p.name}</td><td>{p.gp}</td><td>{format(p.ppg)}</td><td>{format(p.rpg)}</td>
                    <td>{format(p.apg)}</td><td>{format(p.spg)}</td><td>{format(p.fg)}%</td><td>{format(p.per)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {currentTab === 'charts' && (
        <div className="grid-2">
          <div className="card panel-pad">
            <div className="panel-title">Rebounds</div>
            <div className="chart-container"><Bar data={{ labels: byDesc(adv, "rpg").map(x => x.name.split(" ")[0]), datasets: [{ label: "RPG", data: byDesc(adv, "rpg").map(x => x.rpg), backgroundColor: "#34d399" }] }} options={chartOpts} /></div>
          </div>
          <div className="card panel-pad">
            <div className="panel-title">Top 3 All-Around Radar</div>
            <div className="chart-container">
              <Radar
                data={{
                  labels: ["PPG", "RPG", "APG", "SPG", "BPG", "FG/10"],
                  datasets: byDesc(adv, "per").slice(0, 3).map((p, i) => ({
                    label: p.name.split(" ")[0], data: [p.ppg, p.rpg, p.apg, p.spg, p.bpg, p.fg / 10],
                    borderColor: ["#60a5fa", "#34d399", "#fbbf24"][i], backgroundColor: ["rgba(96,165,250,.18)", "rgba(52,211,153,.18)", "rgba(251,191,36,.18)"][i]
                  }))
                }}
                options={{ ...chartOpts, scales: { r: { grid: { color: "rgba(255,255,255,.08)" }, pointLabels: { color: "#dbe5ff" }, ticks: { backdropColor: "transparent" } } } }}
              />
            </div>
          </div>
        </div>
      )}

      {currentTab === 'advanced' && (
        <div className="card panel-pad">
          <div className="panel-title">Advanced Metrics (PER)</div>
          <div className="adv-note">PER = PPG + RPG + APG + SPG + BPG − TOV</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Player</th><th>PER</th><th>Offense</th><th>Defense</th><th>Profile</th></tr></thead>
              <tbody>
                {byDesc(adv, "per").map(p => (
                  <tr key={p.name}>
                    <td>{p.name}</td><td>{format(p.per)}</td><td>{format(p.off)}</td><td>{format(p.def)}</td>
                    <td>{p.per > 7 ? "Star impact" : p.per > 4 ? "Two-way value" : "Role player"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------
// 🏀 Basketball JV Component
// ----------------------------------------------------
function BasketballJV({ data, currentTab }) {
  const players = byDesc(data.players, "pts");
  const top = players[0];

  return (
    <div>
      {currentTab === 'overview' && (
        <>
          <div className="card mvp">
            <small>Game Leader · Scoring</small>
            <h2>{top.name}</h2>
            <div className="mvp-stats">
              <div><b>{top.pts}</b><span>Points</span></div>
              <div><b>{top.reb}</b><span>Rebounds</span></div>
              <div><b>{top.ast}</b><span>Assists</span></div>
            </div>
          </div>
          <div className="kpi-grid" style={{ marginTop: '16px' }}>
            <div className="card kpi"><div className="label">Total Points</div><div className="value">{sum(players, "pts")}</div><div className="meta">Team total</div></div>
            <div className="card kpi"><div className="label">Total Rebounds</div><div className="value">{sum(players, "reb")}</div><div className="meta">Team total</div></div>
          </div>
          <div className="grid-2" style={{ marginTop: '16px' }}>
            <div className="card panel-pad">
              <div className="panel-title">Scoring vs NLCS</div>
              <div className="chart-container"><Bar data={{ labels: players.map(x => x.name.split(" ")[0]), datasets: [{ label: "Points", data: players.map(x => x.pts), backgroundColor: "#60a5fa" }] }} options={chartOpts} /></div>
            </div>
          </div>
        </>
      )}
      {currentTab === 'roster' && (
        <div className="card panel-pad">
          <div className="panel-title">JV Game Box Score</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>#</th><th>Player</th><th>PTS</th><th>REB</th><th>AST</th><th>STL</th><th>BLK</th><th>TOV</th><th>FOUL</th></tr></thead>
              <tbody>
                {players.map(p => (
                  <tr key={p.name}><td>{p.num}</td><td>{p.name}</td><td>{p.pts}</td><td>{p.reb}</td><td>{p.ast}</td><td>{p.stl}</td><td>{p.blk}</td><td>{p.tov}</td><td>{p.foul}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {currentTab === 'charts' && (
        <div className="card panel-pad">
          <div className="panel-title">Rebounds & Assists</div>
          <div className="chart-container">
            <Bar data={{ labels: players.map(x => x.name.split(" ")[0]), datasets: [{ label: "Rebounds", data: players.map(x => x.reb), backgroundColor: "#34d399" }, { label: "Assists", data: players.map(x => x.ast), backgroundColor: "#fbbf24" }] }} options={chartOpts} />
          </div>
        </div>
      )}
      {currentTab === 'advanced' && (
        <div className="card panel-pad">
          <div className="panel-title">Quick Impact Score</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Player</th><th>Impact Score</th><th>Notes</th></tr></thead>
              <tbody>
                {players.map(p => {
                  const impact = p.pts + p.reb * 0.8 + p.ast * 1.2 + p.stl * 1.5 - p.tov;
                  return (<tr key={p.name}><td>{p.name}</td><td>{format(impact)}</td><td>{impact >= 7 ? "High impact" : impact >= 3 ? "Useful contribution" : "Low-usage"}</td></tr>);
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------
// 🏐 Volleyball Component
// ----------------------------------------------------
function Volleyball({ data, currentTab }) {
  const adv = getVolleyballAdv(data.players);
  const topSpike = byDesc(adv, "spikes")[0];

  return (
    <div>
      {currentTab === 'overview' && (
        <>
          <div className="card mvp">
            <small>Season Leader · Spikes</small>
            <h2>{topSpike.name}</h2>
            <div className="mvp-stats">
              <div><b>{topSpike.spikes}</b><span>Spikes</span></div>
              <div><b>{topSpike.aces}</b><span>Aces</span></div>
              <div><b>{topSpike.digs}</b><span>Digs</span></div>
            </div>
          </div>
          <div className="grid-2" style={{ marginTop: '16px' }}>
            <div className="card panel-pad"><div className="panel-title">Spike Leaders</div><div className="chart-container"><Bar data={{ labels: byDesc(adv, "spikes").map(x => x.name.split(" ")[0]), datasets: [{ label: "Spikes", data: byDesc(adv, "spikes").map(x => x.spikes), backgroundColor: "#60a5fa" }] }} options={chartOpts} /></div></div>
            <div className="card panel-pad"><div className="panel-title">Aces Leaders</div><div className="chart-container"><Bar data={{ labels: byDesc(adv, "aces").map(x => x.name.split(" ")[0]), datasets: [{ label: "Aces", data: byDesc(adv, "aces").map(x => x.aces), backgroundColor: "#fbbf24" }] }} options={chartOpts} /></div></div>
          </div>
        </>
      )}
      {currentTab === 'roster' && (
        <div className="card panel-pad">
          <div className="panel-title">Full Roster Stats</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>#</th><th>Player</th><th>Aces</th><th>Digs</th><th>Blocks</th><th>Spikes</th><th>AES</th></tr></thead>
              <tbody>
                {byDesc(adv, "aes").map((p) => (<tr key={p.name}><td>{p.num}</td><td>{p.name}</td><td>{p.aces}</td><td>{p.digs}</td><td>{p.blocks}</td><td>{p.spikes}</td><td>{format(p.aes, 0)}</td></tr>))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {currentTab === 'charts' && (
        <div className="card panel-pad">
          <div className="panel-title">All Stats Comparison</div>
          <div className="chart-container">
            <Bar data={{ labels: adv.map(x => x.name.split(" ")[0]), datasets: [{ label: "Spikes", data: adv.map(x => x.spikes), backgroundColor: "#60a5fa" }, { label: "Aces", data: adv.map(x => x.aces), backgroundColor: "#fbbf24" }, { label: "Digs", data: adv.map(x => x.digs), backgroundColor: "#34d399" }, { label: "Blocks", data: adv.map(x => x.blocks), backgroundColor: "#a78bfa" }] }} options={chartOpts} />
          </div>
        </div>
      )}
      {currentTab === 'advanced' && (
        <div className="card panel-pad">
          <div className="panel-title">Attack Efficiency Score (AES)</div>
          <div className="adv-note">AES = Spikes×2 + Aces×2.5 + Blocks×1.5</div>
          <div className="chart-container"><Bar data={{ labels: byDesc(adv, "aes").map(x => x.name.split(" ")[0]), datasets: [{ label: "AES", data: byDesc(adv, "aes").map(x => x.aes), backgroundColor: "#5eead4" }] }} options={chartOpts} /></div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------
// 🥍 Lacrosse Component
// ----------------------------------------------------
function Lacrosse({ data, currentTab }) {
  const adv = getLaxAdv(data.players);
  const topLIS = byDesc(adv, "lis")[0];

  return (
    <div>
      {currentTab === 'overview' && (
        <>
          <div className="card mvp">
            <small>Impact Leader</small>
            <h2>{topLIS.name}</h2>
            <div className="mvp-stats">
              <div><b>{topLIS.goals}</b><span>Goals</span></div>
              <div><b>{topLIS.gbs}</b><span>Ground Balls</span></div>
              <div><b>{topLIS.ct}</b><span>Caused TO</span></div>
            </div>
          </div>
          <div className="grid-2" style={{ marginTop: '16px' }}>
            <div className="card panel-pad"><div className="panel-title">Goals Leaders</div><div className="chart-container"><Bar data={{ labels: byDesc(adv, "goals").filter(x => x.goals > 0).map(x => x.name.split(" ")[0]), datasets: [{ label: "Goals", data: byDesc(adv, "goals").filter(x => x.goals > 0).map(x => x.goals), backgroundColor: "#60a5fa" }] }} options={chartOpts} /></div></div>
            <div className="card panel-pad"><div className="panel-title">Ground Balls</div><div className="chart-container"><Bar data={{ labels: byDesc(adv, "gbs").map(x => x.name.split(" ")[0]), datasets: [{ label: "Ground Balls", data: byDesc(adv, "gbs").map(x => x.gbs), backgroundColor: "#34d399" }] }} options={chartOpts} /></div></div>
          </div>
        </>
      )}
      {currentTab === 'roster' && (
        <div className="card panel-pad">
          <div className="panel-title">Full Roster Stats</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Player</th><th>Role</th><th>Goals</th><th>Assists</th><th>GBs</th><th>CT</th><th>LIS</th></tr></thead>
              <tbody>
                {byDesc(adv, "lis").map((p) => (<tr key={p.name}><td>{p.name}</td><td>{p.role}</td><td>{p.goals}</td><td>{p.assists}</td><td>{p.gbs}</td><td>{p.ct}</td><td>{format(p.lis)}</td></tr>))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {currentTab === 'charts' && (
        <div className="grid-2">
          <div className="card panel-pad">
            <div className="panel-title">Goals Share</div>
            <div className="chart-container"><Doughnut data={{ labels: adv.filter(x => x.goals > 0).map(x => x.name.split(" ")[0]), datasets: [{ data: adv.filter(x => x.goals > 0).map(x => x.goals), backgroundColor: ["#60a5fa", "#34d399", "#fbbf24", "#f87171", "#a78bfa"] }] }} options={{ ...chartOpts, maintainAspectRatio: false }} /></div>
          </div>
          <div className="card panel-pad">
            <div className="panel-title">Offense vs Defense</div>
            <div className="chart-container"><Scatter data={{ datasets: [{ label: "Players", data: adv.map(x => ({ x: x.off, y: x.def, label: x.name })), backgroundColor: "#fbbf24", pointRadius: 6 }] }} options={{ ...chartOpts, plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.raw.label}: Off ${ctx.raw.x}, Def ${ctx.raw.y}` } } } }} /></div>
          </div>
        </div>
      )}
      {currentTab === 'advanced' && (
        <div className="card panel-pad">
          <div className="panel-title">Lacrosse Impact Score (LIS)</div>
          <div className="adv-note">LIS = Goals×2 + Assists×1.5 + Ground Balls + Caused Turnovers×1.5</div>
          <div className="chart-container"><Bar data={{ labels: byDesc(adv, "lis").map(x => x.name.split(" ")[0]), datasets: [{ label: "LIS", data: byDesc(adv, "lis").map(x => x.lis), backgroundColor: "#5eead4" }] }} options={chartOpts} /></div>
        </div>
      )}
    </div>
  );
}

export default App;